/**
 * toolDomain — interprets `io.doclm.tool.*`, and by registration alias
 * `io.doclm.retrieval.*` (retrieval is a sub-part of tool).
 *
 * The primary step domain, and the only one with captured payloads for every
 * phase — so it doubles as the reference implementation of the interpreter
 * contract.
 */

import { isWebSourceFormat } from 'src/content/ChatV2/constants';
import { inferFileExtension } from 'src/content/ChatV2/lib/fileType';
import { asRecord, asString } from 'src/content/ChatV2/lib/guards';
import {
  FALLBACK_STEP_LABEL,
  STEP_KIND_LABEL
} from 'src/content/ChatV2/steps/labels';
import { contentText } from 'src/content/ChatV2/steps/model/contentText';
import type {
  DomainInterpreter,
  NormalizedEvent
} from 'src/content/ChatV2/steps/model/types';
import { toolDisplayName } from 'src/content/ChatV2/steps/toolTemplates';
import { EVENT_DOMAIN } from 'src/content/ChatV2/steps/wire/eventType';
import {
  type Step,
  STEP_DETAIL_KIND,
  STEP_KIND,
  STEP_SEARCH_SOURCE,
  STEP_STATUS,
  type StepDetail,
  type StepSearchResult
} from 'src/content/ChatV2/types/steps';

const TOOL_KIND_TABLE: ReadonlyArray<[prefix: string, kind: STEP_KIND]> = [
  ['web_search', STEP_KIND.SEARCHING_WEB],
  ['web_fetch', STEP_KIND.SEARCHING_WEB],
  ['enterprise__list_documents', STEP_KIND.LISTING_DOCUMENTS],
  ['enterprise', STEP_KIND.SEARCHING_WORKSPACE],
  ['code_executor', STEP_KIND.EXECUTING_CODE],
  ['code_interpreter', STEP_KIND.EXECUTING_CODE],
  ['python', STEP_KIND.RUNNING_SCRIPT],
  ['image', STEP_KIND.CHECKING_IMAGE],
  ['nano-banana', STEP_KIND.CHECKING_IMAGE],
  ['imagen', STEP_KIND.CHECKING_IMAGE],
  ['attachment', STEP_KIND.CHECKING_FILES],
  ['file', STEP_KIND.CHECKING_FILES]
];

const TOOL_KIND_PREFIXES = [...TOOL_KIND_TABLE].sort(
  (a, b) => b[0].length - a[0].length
);

function toolName(event: NormalizedEvent): string {
  const value = event.data.tool_name;
  return typeof value === 'string' ? value : '';
}

function kindForToolName(name: string): STEP_KIND {
  const lower = name.toLowerCase();
  for (const [prefix, kind] of TOOL_KIND_PREFIXES) {
    if (lower.startsWith(prefix)) {
      return kind;
    }
  }
  return STEP_KIND.TOOL_SEARCH;
}

const QUERY_KEYS = ['search_phrase', 'query', 'q', 'question'] as const;

function queryOf(
  input: Record<string, unknown> | undefined
): string | undefined {
  if (!input) {
    return undefined;
  }
  for (const key of QUERY_KEYS) {
    const value = input[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
  }
  return undefined;
}

function isFailureStatus(status: unknown): boolean {
  return typeof status === 'string' && status !== '' && status !== 'success';
}

function extractSources(data: Record<string, unknown>): unknown[] | undefined {
  const artifact = asRecord(data.artifact);
  const source = artifact?.source;
  return Array.isArray(source) && source.length > 0 ? source : undefined;
}

export const toolDomain: DomainInterpreter = {
  domain: EVENT_DOMAIN.TOOL,

  identify(event) {
    const id = event.data.tool_call_id;
    return typeof id === 'string' && id !== '' ? id : undefined;
  },

  kind(event) {
    return kindForToolName(toolName(event));
  },

  apply(step, event) {
    const next: Step = { ...step };
    const name = toolName(event);
    if (name !== '') {
      next.kind = kindForToolName(name);
    }

    const callId = event.data.tool_call_id;
    if (typeof callId === 'string' && callId !== '') {
      next.toolCallId = callId;
    }

    const authoritativeInput = asRecord(event.data.input);
    const deltaInput = asRecord(event.data.delta);
    const incoming = authoritativeInput ?? deltaInput;
    if (incoming) {
      next.inputPartial = { ...(step.inputPartial ?? {}), ...incoming };
    }

    if (event.channel === 'output' && typeof event.data.delta === 'string') {
      next.outputText = (step.outputText ?? '') + event.data.delta;
    } else if (
      event.phaseRaw === 'completed' &&
      typeof event.data.content === 'string'
    ) {
      next.outputText = event.data.content;
    }

    if (isFailureStatus(event.data.status)) {
      next.status = STEP_STATUS.FAILED;
    }

    const sources = extractSources(event.data);
    if (sources) {
      next.sources = sources;
    }

    next.label =
      toolDisplayName(name) ??
      STEP_KIND_LABEL[next.kind] ??
      FALLBACK_STEP_LABEL;
    next.labelQuery = queryOf(next.inputPartial);

    return next;
  },

  settle(entry, ctx) {
    if (entry.type !== 'tool') {
      return null;
    }
    const failed = isFailureStatus(entry.status);
    const name = ctx?.toolName ?? entry.tool_name;
    const kind = name ? kindForToolName(name) : undefined;
    const input = ctx?.input;

    return {
      status: failed ? STEP_STATUS.FAILED : STEP_STATUS.DONE,
      toolCallId: entry.tool_call_id,
      kind,
      inputPartial: input,
      label:
        toolDisplayName(name) ??
        (kind === undefined
          ? undefined
          : (STEP_KIND_LABEL[kind] ?? FALLBACK_STEP_LABEL)),
      labelQuery: kind === undefined ? undefined : queryOf(input),
      outputText: contentText(entry.content),
      sources: Array.isArray(entry.artifact?.source)
        ? entry.artifact?.source
        : undefined
    } as Partial<Step>;
  },

  detail(step): StepDetail | undefined {
    const input = step.inputPartial ?? {};

    if (
      step.kind === STEP_KIND.EXECUTING_CODE ||
      step.kind === STEP_KIND.RUNNING_SCRIPT
    ) {
      const code =
        (typeof input.code_snippet === 'string' && input.code_snippet) ||
        (typeof input.code === 'string' && input.code) ||
        (typeof input.command === 'string' && input.command) ||
        '';
      if (code === '') {
        return undefined;
      }
      return {
        kind: STEP_DETAIL_KIND.CODE,
        language:
          typeof input.language === 'string' ? input.language : 'python',
        input: code,
        output: step.outputText
      };
    }

    if (
      step.kind === STEP_KIND.SEARCHING_WEB ||
      step.kind === STEP_KIND.SEARCHING_WORKSPACE ||
      step.kind === STEP_KIND.LISTING_DOCUMENTS ||
      step.kind === STEP_KIND.CHECKING_FILES
    ) {
      const results = toSearchResults(step.sources);
      if (results.length === 0) {
        return undefined;
      }
      return {
        kind: STEP_DETAIL_KIND.SEARCH,
        source:
          step.kind === STEP_KIND.SEARCHING_WEB
            ? STEP_SEARCH_SOURCE.WEB
            : STEP_SEARCH_SOURCE.ENTERPRISE,
        results
      };
    }

    return undefined;
  }
};

function toSearchResults(sources: unknown): StepSearchResult[] {
  if (!Array.isArray(sources)) {
    return [];
  }
  return sources.flatMap((raw, index) => {
    const s = asRecord(raw);
    const title =
      s &&
      (asString(s.display_name) ?? asString(s.name) ?? asString(s.title));
    if (!s || !title) {
      return [];
    }
    const isWeb =
      isWebSourceFormat(s.format) ||
      isWebSourceFormat(s.original_format) ||
      s.type === 'url';
    return [
      {
        id: `${String(s.document_id ?? s.id ?? index)}:${index}`,
        title,
        ...(isWeb ? webFields(s) : documentFields(s))
      }
    ];
  });
}

function hostOf(link: string | undefined): string | undefined {
  if (!link) {
    return undefined;
  }
  try {
    return new URL(link).hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

function webFields(s: Record<string, unknown>): Partial<StepSearchResult> {
  const favicon = asString(s.favicon);
  return {
    subtitle: asString(s.source) ?? hostOf(asString(s.link)),
    faviconUrl: favicon?.startsWith('http') ? favicon : undefined
  };
}

function documentFields(s: Record<string, unknown>): Partial<StepSearchResult> {
  return {
    page: typeof s.page === 'number' ? s.page : undefined,
    extension:
      asString(s.original_format) ??
      inferFileExtension(asString(s.display_name) ?? asString(s.name) ?? '') ??
      asString(s.format)
  };
}
